import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, KeyRound, Loader2, MailCheck } from "lucide-react";

/** Self-serve forgot-password flow.
 *  If Resend is configured, the user gets a one-time reset link in their inbox (1-hour TTL).
 *  If Resend is NOT configured, the backend falls back to returning the reset link in the
 *  API response so the admin can still recover access manually.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setEmailSent(Boolean(data?.email_sent));
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not request reset. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0A192F] via-[#0F2238] to-[#0A192F] p-6">
      <div className="w-full max-w-md">
        <h1 className="font-display text-3xl text-white tracking-tight text-center">Forgot password</h1>
        <p className="text-sm text-slate-400 mt-2 mb-6 text-center">
          Enter your email and we'll send a secure reset link via your account administrator.
        </p>
        <div className="surface-card p-7" data-testid="forgot-password-card">
          {done ? (
            <div className="text-center" data-testid="forgot-password-done">
              <MailCheck className="h-10 w-10 mx-auto" style={{ color: "#2DD4BF" }} />
              <h2 className="font-display text-xl text-slate-900 mt-3">
                {emailSent ? "Check your inbox" : "Check with your administrator"}
              </h2>
              <p className="text-sm text-slate-600 mt-2">
                {emailSent ? (
                  <>
                    If an account exists for <span className="font-semibold">{email}</span>, a one-time reset link
                    is on its way. The link is valid for <strong>1 hour</strong>.
                  </>
                ) : (
                  <>
                    If an account exists for <span className="font-semibold">{email}</span>, your administrator has been notified
                    and will send you a new temporary password.
                  </>
                )}
              </p>
              <p className="text-xs text-slate-500 mt-3">
                Didn't get it? Check your spam folder, or email <a href="mailto:intel@therevenuecycleiq.com" className="text-teal-700 font-semibold">intel@therevenuecycleiq.com</a>
              </p>
              <Link to="/login" className="inline-flex items-center text-xs text-slate-600 hover:text-slate-900 mt-6">
                <ArrowLeft className="h-3 w-3 mr-1.5" /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-password-email"
                  className="h-11 rounded-xl mt-1.5"
                  placeholder="you@hospital.com"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="ai-cta rounded-xl px-5 h-11 w-full"
                data-testid="forgot-password-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
                Request reset
              </Button>
              <Link to="/login" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-3 w-3 mr-1.5" /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
